import type { RoasterListItem } from "@bcc/components/admin/roaster-list.types";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import type { DataTableRow } from "@client-shared";
import { generateTestId } from "@client-shared";
import type { BCCVendor } from "@shared/types";

const base = "AdminRoastersPage";

export const setAdminRoastersPageTestId = (identifier: string): string => generateTestId(base, identifier);

export const TEST_IDS = {
  vendorsLoadError: setAdminRoastersPageTestId("vendors-load-error"),
  vendorSelectError: setAdminRoastersPageTestId("vendor-select-error"),
  userMenuTrigger: setAdminRoastersPageTestId("user-menu-trigger"),
  userMenuContent: setAdminRoastersPageTestId("user-menu-content"),
  userName: setAdminRoastersPageTestId("user-name"),
  userEmail: setAdminRoastersPageTestId("user-email"),
  userRole: setAdminRoastersPageTestId("user-role"),
  logoutButton: setAdminRoastersPageTestId("logout-button"),
} as const;

interface IdTokenPayload {
  custom_attributes?: {
    UserFirstName?: string;
    UserLastName?: string;
    profileName?: string;
    UserEmail?: string;
  };
}

export interface ParsedUserDetails {
  name?: string;
  email?: string;
}

const getStringValue = (value: unknown): string | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
};

const parseIdTokenPayload = (idToken: string): IdTokenPayload | undefined => {
  const [, payloadSegment] = idToken.split(".");
  if (!payloadSegment) {
    return undefined;
  }

  try {
    const normalizedPayload = payloadSegment.replaceAll("-", "+").replaceAll("_", "/");
    const requiredPadding = (4 - (normalizedPayload.length % 4)) % 4;
    const paddedPayload = `${normalizedPayload}${"=".repeat(requiredPadding)}`;
    const decodedPayload = globalThis.atob(paddedPayload);
    const parsedPayload: unknown = JSON.parse(decodedPayload);

    if (typeof parsedPayload !== "object" || parsedPayload === null) {
      return undefined;
    }
    return parsedPayload as IdTokenPayload;
  } catch {
    return undefined;
  }
};

export const parseUserDetailsFromIdToken = (idToken: string | null | undefined): ParsedUserDetails => {
  if (!idToken) {
    return {};
  }

  const tokenPayload = parseIdTokenPayload(idToken);
  if (!tokenPayload) {
    return {};
  }

  const firstName = getStringValue(tokenPayload.custom_attributes?.UserFirstName);
  const lastName = getStringValue(tokenPayload.custom_attributes?.UserLastName);
  const name = [firstName, lastName].filter(Boolean).join(" ") || undefined;

  return {
    name,
    email: getStringValue(tokenPayload.custom_attributes?.UserEmail),
  };
};

export const getVendorKey = (vendor: BCCVendor): string => {
  return `${vendor.region}:${vendor.vendorId}`;
};

export const getAccountTypeLabel = (accountType: string): RoasterListItem["accountType"] => {
  switch (accountType) {
    case "beanz-connect":
      return "beanz";
    case "shipstation":
      return "shipstation";
    default:
      return "beanz";
  }
};

export const mapVendorToRoasterListItem = (vendor: BCCVendor): DataTableRow<RoasterListItem> => {
  return {
    id: getVendorKey(vendor),
    roasterName: vendor.name || vendor.vendorId,
    roasterEmail: vendor.vendorId,
    platform: vendor.type === "shipstation" ? "woocommerce" : "shopify",
    status: "active",
    lastActive: new Date().toISOString(),
    accountType: getAccountTypeLabel(vendor.type),
  };
};

export const getNavigationTarget = (): string => {
  const redirectUrl = localStorage.getItem("redirectUrl");
  if (redirectUrl) {
    localStorage.removeItem("redirectUrl");
    return redirectUrl;
  }
  return ROUTE_PATHS.HOME;
};
